import GroupNameEdit from "../GroupNameEdit/GroupNameEdit.jsx";
import DropdownMenu from "../../../../../../design-system/components/DropdownMenu/DropdownMenu.jsx";
import GroupNameEditButton from "../GroupNameEditButton/GroupNameEditButton.jsx";
import GroupDeleteButton from "../GroupDeleteButton/GroupDeleteButton.jsx";
import StudentList from "../StudentList/StudentList.jsx";
import { useStudentDrop } from "../../../../../../hooks/useStudentDrop.jsx";
import styles from "./StudentGroup.css.ts";

/**
 * A functional component that displays a list of students in a group or a placeholder message if the group is empty.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.teamMembers - An array containing the members of the group.
 * @param {Function} props.drop - A ref callback used to enable drag-and-drop functionality for the group container.
 * @param {boolean} props.isOver - A flag indicating whether a draggable item is currently being hovered over the group container.
 * @param {Object} props.workshop - An object containing information about the workshop.
 */
const StudentsInGroup = ({ teamMembers, drop, isOver, workshop }) => {
  return teamMembers.length > 0 ? (
    <StudentList
      drop={drop}
      isOver={isOver}
      students={teamMembers}
      workshop={workshop}
      isInGroup
    />
  ) : (
    <div className={styles.containerNoMemberInGroup} ref={drop}>
      <p className={styles.noMemberInGroupText}>Diese Gruppe ist noch leer</p>
    </div>
  );
};

/**
 * Represents a student group component with functionality for managing students in a workshop context.
 * It allows interacting with drop targets, editing group names, utilizing external tools, and managing group members.
 *
 * @param {Object} params - An object containing various properties required by the component.
 * @param {Object} params.workshop - The workshop to which the student group belongs.
 * @param {Object} params.smallTeam - The specific team or group associated with the component.
 * @param {Array} params.teamMembers - An array of members within the specific team or group.
 * @param {Function} params.moveStudent - Function to handle moving students between groups.
 * @return {JSX.Element} A JSX element representing the student group.
 */
export function StudentGroup({
  workshop,
  smallTeam,
  teamMembers,
  moveStudent,
}) {
  const { isOver, drop } = useStudentDrop({
    targetGroupId: smallTeam.id,
    moveStudent,
  });

  return (
    <div ref={drop} className={styles.group({ over: isOver })}>
      <div className={styles.header}>
        <div className={styles.headerFront}>
          <h3 className={styles.subHeadline}>
            <GroupNameEdit
              workshopId={workshop.id}
              smallTeamId={smallTeam.id}
              groupName={smallTeam.name}
            />
          </h3>
        </div>

        <div className={styles.headerBack}>
          <DropdownMenu direction="left" theme="dark">
            <GroupNameEditButton smallTeamId={smallTeam.id} />
            <hr className={styles.divider} />
            <GroupDeleteButton workshop={workshop} smallTeam={smallTeam} />
          </DropdownMenu>
        </div>
      </div>

      <span data-cy="cy-students-in-smallgroups">
        <StudentsInGroup
          teamMembers={teamMembers}
          drop={drop}
          isOver={isOver}
          workshop={workshop}
        />
      </span>
    </div>
  );
}
